import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@invent/wl-ui-kit-next/ui';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@invent/wl-ui-kit-next';
import { FilterChip, StatusChip } from '@/design';
import { U4_FLAGS, ddStatusChip, u4FlagOf, u4IssueWords, type IDdDisclosures } from '../../dd';
import type { IRevealState } from '../person-hub/disclosures';
import { LIST_SURVEYS } from '../../lists';

/**
 * What the two DD screens and the record page's block draw alike: the survey picker over the
 * Lists' surveys (every one with a DD call flag of its own carries the DD group), a DD status
 * as its chip, and a booked call's Overdue.
 */

/** The address's `?survey=` where it names one of the Lists' surveys, else FA. */
export function surveyParam(params: URLSearchParams): string {
  const asked = params.get('survey');
  return LIST_SURVEYS.some((survey) => survey.code === asked) ? asked! : 'FA';
}

/** The address's `?survey=`s (SR-499: one key each, several at once), in the Lists' order — those
 *  naming one of the Lists' surveys; none, FA. */
export function surveysParam(params: URLSearchParams): string[] {
  const asked = params.getAll('survey');
  const known = LIST_SURVEYS.filter((survey) => asked.includes(survey.code)).map((survey) => survey.code);
  return known.length > 0 ? known : ['FA'];
}

/** The surveys chosen, in the Lists' order, after `code` is pressed: on or off — or null for
 *  the last one, which stays, as the queue always reads one. */
function toggled(chosen: readonly string[], code: string): Nullable<string[]> {
  const next = chosen.includes(code) ? chosen.filter((one) => one !== code) : [...chosen, code];
  return next.length === 0
    ? null
    : LIST_SURVEYS.filter((survey) => next.includes(survey.code)).map((survey) => survey.code);
}

/** A press on a survey: the choice moved, or nothing at all for the last one. */
const pressing = (chosen: readonly string[], onChoose: (surveys: string[]) => void) => (code: string) => {
  const next = toggled(chosen, code);
  if (next) onChoose(next);
};

const surveysWords = (chosen: readonly string[]) =>
  chosen.map((code) => LIST_SURVEYS.find((survey) => survey.code === code)?.label ?? code).join(', ');

/**
 * The surveys the queue reads (SR-499), several at once: each a toggle in the header's row, where
 * the tabs stood — pressed, it joins the queue or leaves it; the last one stays.
 */
export function DdSurveyToggles({
  chosen,
  onChoose
}: {
  chosen: readonly string[];
  onChoose: (surveys: string[]) => void;
}) {
  const press = pressing(chosen, onChoose);
  return (
    <div role="group" aria-label="Surveys" className="-mx-1 flex flex-wrap items-center gap-1.5">
      {LIST_SURVEYS.map((survey) => {
        const on = chosen.includes(survey.code);
        const last = on && chosen.length === 1;
        return (
          <button
            key={survey.code}
            type="button"
            role="checkbox"
            aria-checked={on}
            aria-disabled={last || undefined}
            title={last ? 'The queue reads at least one survey' : on ? `Leave ${survey.label} out` : `Add ${survey.label}`}
            data-survey-choice={survey.code}
            onClick={() => press(survey.code)}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[0.8125rem] outline-none focus-visible:ring-2 focus-visible:ring-ring',
              on
                ? 'border-[var(--color-royal-blue)] bg-[var(--color-slight-blue)] font-medium text-[var(--color-royal-blue)]'
                : 'border-border bg-white text-[var(--veritant-royal-70)] hover:bg-muted'
            )}
          >
            {on && <Check className="size-3.5" aria-hidden />}
            {survey.label}
          </button>
        );
      })}
    </div>
  );
}

/** The same choice in the compact bar: a picklist of the surveys, ticked many at once. */
export function DdSurveyPicklist({
  chosen,
  onChoose
}: {
  chosen: readonly string[];
  onChoose: (surveys: string[]) => void;
}) {
  const press = pressing(chosen, onChoose);
  return (
    <FilterChip label="Survey" summary={surveysWords(chosen)}>
      <div role="group" aria-label="Surveys" className="grid min-w-44 gap-0.5">
        {LIST_SURVEYS.map((survey) => {
          const on = chosen.includes(survey.code);
          return (
            <button
              key={survey.code}
              type="button"
              role="checkbox"
              aria-checked={on}
              className="flex items-center gap-2 rounded px-1.5 py-1 text-left outline-none select-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => press(survey.code)}
            >
              <span
                aria-hidden
                className={cn(
                  'grid size-4 shrink-0 place-items-center rounded-sm border',
                  on ? 'border-[var(--color-teal)] bg-[var(--color-teal)] text-white' : 'border-border bg-white'
                )}
              >
                {on && <Check className="size-3" />}
              </span>
              {survey.label}
            </button>
          );
        })}
      </div>
    </FilterChip>
  );
}

export function DdSurveySelect({ value, onChange }: { value: string; onChange: (code: string) => void }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-52" aria-label="Survey">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LIST_SURVEYS.map((survey) => (
          <SelectItem key={survey.code} value={survey.code}>
            {survey.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** A booked call whose time has passed (SR-424), on the block, the queue and My tasks alike; the
 *  span is the suites' `data-dd-overdue` hook. */
export function DdOverdueChip() {
  return (
    <span data-dd-overdue="">
      <StatusChip tone="danger">Overdue</StatusChip>
    </span>
  );
}

export function DdStatusChip({ status, dot = false }: { status: string; dot?: boolean }) {
  const chip = ddStatusChip(status);
  return (
    <StatusChip tone={chip.tone} dot={dot}>
      {chip.label}
    </StatusChip>
  );
}

/** The record page's Disclosures section, as a link lands on it: the section's own anchor, opened
 *  and scrolled to once its part has drawn (`IRevealState`). */
export const DISCLOSURES_REVEAL: IRevealState = { reveal: 'disclosures' };

/**
 * The U4 flag (SR-498) — issue found, clear, not checked — a link to the record's Disclosures
 * section; on the queue and in the panel alike, and none with no CRD. What an issue is made of is
 * its tooltip, and rides beside it unless `bare`.
 */
export function U4FlagLink({ facts, bare = false }: { facts: IDdDisclosures; bare?: boolean }) {
  const flag = u4FlagOf(facts);
  if (flag === 'no_crd') return null;
  const chip = U4_FLAGS[flag];
  const issue = flag === 'issue' ? u4IssueWords(facts) : '';
  return (
    <Link
      to={facts.link}
      state={DISCLOSURES_REVEAL}
      // The chip whole, the words wrapping under it on a narrow screen (a phone's record page).
      className={cn(
        'inline-flex items-center underline-offset-4 hover:underline',
        bare ? 'whitespace-nowrap' : 'flex-wrap gap-x-1.5 gap-y-0.5'
      )}
      title={issue || undefined}
      aria-label={`${chip.label}${issue ? ` — ${issue}` : ''}. Open the disclosures on the record`}
      data-u4-flag={flag}
    >
      <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
      {!bare && issue && <span className="text-[var(--color-sapphire)]">{issue}</span>}
    </Link>
  );
}
